import { useToast } from "@smart/bridge-intake-components-dom";
import {
  useInFlightSubmissionCount,
  usePublishForm,
  useUpdateFormPublishStatus,
} from "@smart/manage-gql-client-dom";
import { useAnalyticsEvent } from "@smart/manage-hooks-dom";

import { usePublish } from "./context";
import { PublishModal } from "./publish-modal";

export { PublishFormPopup } from "./popup";
export { PublishProvider, usePublish } from "./context";

type PublishFormProps = {
  formUri: string;
  formTitle: string;
  onPublished?: () => void;
};

export const PublishForm = ({
  formUri,
  formTitle,
  onPublished,
}: PublishFormProps) => {
  const { setToasts } = useToast();
  const { publishEvent } = useAnalyticsEvent();
  const { publishModalOpen, setPublishModalOpen } = usePublish();

  const { result: inFlightSubmissionCount, loading: loadingInFlightCount } =
    useInFlightSubmissionCount({ formUri }, { skip: !publishModalOpen });

  const [publishForm, { loading: publishingForm }] = usePublishForm();
  const [updateFormPublishStatus, { loading: updatingFormPublishStatus }] =
    useUpdateFormPublishStatus();

  const onPublishConfirm = async ({
    publishNote,
    applyToInFlight,
  }: {
    publishNote?: string;
    applyToInFlight: boolean;
  }) => {
    try {
      publishEvent?.({
        category: "track",
        name: "IntakeFormVersionPublished",
        screen: "FormBuilder",
        entryPoint: "FormBuilderEditView",
        component: "PublishModal",
        jiraKey: "LINK-3465",
        interactionType: "Active",
        properties: {
          FormName: formTitle,
          ApplyToInFlight: applyToInFlight,
          InFlightSubmissionCount: inFlightSubmissionCount,
        },
      }).catch(console.error);
      await publishForm({
        variables: {
          formUri,
          publishNote,
          applyToInFlight,
        },
      });
      await updateFormPublishStatus({
        variables: {
          uri: formUri,
          hasUnpublishedChanges: false,
        },
      });
      setPublishModalOpen(false);
      setToasts([
        {
          text: "Form has been published.",
          palette: "success",
        },
      ]);
      onPublished?.();
    } catch (e) {
      console.error(e);
      setToasts([
        {
          text: "Could not publish the form. Try again.",
          palette: "destructive",
        },
      ]);
    }
  };

  if (!publishModalOpen) return null;

  return (
    <PublishModal
      open={publishModalOpen}
      onClose={() => setPublishModalOpen(false)}
      loading={
        publishingForm || updatingFormPublishStatus || loadingInFlightCount
      }
      onConfirm={onPublishConfirm}
      inFlightCount={inFlightSubmissionCount}
    />
  );
};
